/**
 * Request Metrics Tracking
 * Collects data on retry attempts, latency, and success rates
 * for monitoring and debugging API resilience
 */

/**
 * Metrics for a single request attempt
 */
export interface RequestAttempt {
  /** Attempt number (1-based) */
  attemptNumber: number;

  /** HTTP status code (0 if network error) */
  statusCode: number;

  /** Time taken in milliseconds */
  duration: number;

  /** Whether attempt succeeded */
  success: boolean;

  /** Error message if failed */
  error?: string;

  /** Timestamp of attempt */
  timestamp: number;

  /** Delay waited before this attempt (ms) */
  delayBeforeMs?: number;
}

/**
 * Complete metrics for a request including all retry attempts
 */
export interface RequestMetrics {
  /** Unique request ID */
  requestId: string;

  /** Endpoint that was called */
  endpoint: string;

  /** HTTP method */
  method: string;

  /** All attempts made (retries included) */
  attempts: RequestAttempt[];

  /** Total time from start to finish (ms) */
  totalDuration: number;

  /** Whether final result was successful */
  success: boolean;

  /** Final HTTP status code */
  finalStatusCode: number;

  /** Number of retries performed */
  retryCount: number;

  /** Total delay spent waiting for retries (ms) */
  totalRetryDelayMs: number;

  /** Start timestamp */
  startTime: number;

  /** End timestamp */
  endTime: number;

  /** Whether circuit breaker rejected this request */
  rejectedByCircuitBreaker: boolean;

  /** Circuit breaker state when request was made */
  circuitBreakerState?: 'CLOSED' | 'OPEN' | 'HALF_OPEN';
}

/**
 * Aggregated metrics across multiple requests
 */
export interface AggregateMetrics {
  /** Total number of requests */
  totalRequests: number;

  /** Number of successful requests */
  successfulRequests: number;

  /** Number of failed requests */
  failedRequests: number;

  /** Success rate percentage (0-100) */
  successRate: number;

  /** Average latency in milliseconds */
  averageLatency: number;

  /** Minimum latency in milliseconds */
  minLatency: number;

  /** Maximum latency in milliseconds */
  maxLatency: number;

  /** Average number of retries per request */
  averageRetries: number;

  /** Requests rejected by circuit breaker */
  circuitBreakerRejections: number;

  /** Total retry delay across all requests (ms) */
  totalRetryDelay: number;

  /** Breakdown by status code */
  statusCodeDistribution: Record<number, number>;

  /** Breakdown by HTTP method */
  methodDistribution: Record<string, number>;
}

/**
 * Metrics collector for API requests
 * Tracks retry attempts, latency, and success rates
 */
export class RequestMetricsCollector {
  private metrics: Map<string, RequestMetrics> = new Map();
  private maxMetricsToKeep: number = 1000;

  /**
   * Create a metrics collector
   * @param maxMetricsToKeep Maximum number of request metrics to retain (default: 1000)
   */
  constructor(maxMetricsToKeep: number = 1000) {
    this.maxMetricsToKeep = maxMetricsToKeep;
  }

  /**
   * Start tracking a new request
   * @param requestId Unique request identifier
   * @param endpoint API endpoint
   * @param method HTTP method
   * @returns Request metrics object to update
   */
  startRequest(requestId: string, endpoint: string, method: string): RequestMetrics {
    const metrics: RequestMetrics = {
      requestId,
      endpoint,
      method,
      attempts: [],
      totalDuration: 0,
      success: false,
      finalStatusCode: 0,
      retryCount: 0,
      totalRetryDelayMs: 0,
      startTime: Date.now(),
      endTime: 0,
      rejectedByCircuitBreaker: false,
    };

    this.metrics.set(requestId, metrics);
    this.pruneMetricsIfNeeded();

    return metrics;
  }

  /**
   * Record an attempt (initial or retry)
   * @param requestId Request identifier
   * @param statusCode HTTP status code
   * @param duration Time taken in milliseconds
   * @param success Whether attempt succeeded
   * @param error Error message if failed
   * @param delayBeforeMs Delay waited before this attempt
   */
  recordAttempt(
    requestId: string,
    statusCode: number,
    duration: number,
    success: boolean,
    error?: string,
    delayBeforeMs?: number
  ): void {
    const metrics = this.metrics.get(requestId);
    if (!metrics) {
      return;
    }

    const attemptNumber = metrics.attempts.length + 1;
    metrics.attempts.push({
      attemptNumber,
      statusCode,
      duration,
      success,
      error,
      timestamp: Date.now(),
      delayBeforeMs,
    });

    if (!success && attemptNumber > 1) {
      metrics.retryCount++;
    }

    if (delayBeforeMs) {
      metrics.totalRetryDelayMs += delayBeforeMs;
    }

    // Update final status on last attempt
    metrics.finalStatusCode = statusCode;
    metrics.success = success;
  }

  /**
   * Mark request as rejected by circuit breaker
   * @param requestId Request identifier
   * @param circuitBreakerState Current circuit breaker state
   */
  recordCircuitBreakerRejection(
    requestId: string,
    circuitBreakerState: 'CLOSED' | 'OPEN' | 'HALF_OPEN'
  ): void {
    const metrics = this.metrics.get(requestId);
    if (metrics) {
      metrics.rejectedByCircuitBreaker = true;
      metrics.circuitBreakerState = circuitBreakerState;
      metrics.endTime = Date.now();
      metrics.totalDuration = metrics.endTime - metrics.startTime;
    }
  }

  /**
   * Complete request tracking
   * @param requestId Request identifier
   */
  completeRequest(requestId: string): void {
    const metrics = this.metrics.get(requestId);
    if (metrics) {
      metrics.endTime = Date.now();
      metrics.totalDuration = metrics.endTime - metrics.startTime;
    }
  }

  /**
   * Get metrics for a specific request
   * @param requestId Request identifier
   * @returns Request metrics or undefined if not found
   */
  getRequestMetrics(requestId: string): RequestMetrics | undefined {
    return this.metrics.get(requestId);
  }

  /**
   * Get aggregated metrics across all requests
   * @returns Aggregate metrics
   */
  getAggregateMetrics(): AggregateMetrics {
    const allMetrics = Array.from(this.metrics.values());

    if (allMetrics.length === 0) {
      return {
        totalRequests: 0,
        successfulRequests: 0,
        failedRequests: 0,
        successRate: 0,
        averageLatency: 0,
        minLatency: 0,
        maxLatency: 0,
        averageRetries: 0,
        circuitBreakerRejections: 0,
        totalRetryDelay: 0,
        statusCodeDistribution: {},
        methodDistribution: {},
      };
    }

    const successful = allMetrics.filter(m => m.success).length;
    const failed = allMetrics.length - successful;
    const durations = allMetrics.map(m => m.totalDuration);
    const retries = allMetrics.map(m => m.retryCount);
    const cbRejections = allMetrics.filter(m => m.rejectedByCircuitBreaker).length;

    const statusCodeDistribution: Record<number, number> = {};
    const methodDistribution: Record<string, number> = {};

    for (const metric of allMetrics) {
      // Status codes from all attempts
      for (const attempt of metric.attempts) {
        statusCodeDistribution[attempt.statusCode] =
          (statusCodeDistribution[attempt.statusCode] || 0) + 1;
      }

      // Methods
      methodDistribution[metric.method] = (methodDistribution[metric.method] || 0) + 1;
    }

    return {
      totalRequests: allMetrics.length,
      successfulRequests: successful,
      failedRequests: failed,
      successRate: (successful / allMetrics.length) * 100,
      averageLatency: durations.reduce((a, b) => a + b, 0) / allMetrics.length,
      minLatency: Math.min(...durations),
      maxLatency: Math.max(...durations),
      averageRetries: retries.reduce((a, b) => a + b, 0) / allMetrics.length,
      circuitBreakerRejections: cbRejections,
      totalRetryDelay: allMetrics.reduce((sum, m) => sum + m.totalRetryDelayMs, 0),
      statusCodeDistribution,
      methodDistribution,
    };
  }

  /**
   * Get metrics for last N requests
   * @param count Number of recent requests to retrieve
   * @returns Array of recent request metrics
   */
  getRecentMetrics(count: number = 10): RequestMetrics[] {
    const allMetrics = Array.from(this.metrics.values());
    return allMetrics
      .sort((a, b) => b.startTime - a.startTime)
      .slice(0, count);
  }

  /**
   * Get metrics filtered by status
   * @param success Filter by success/failure
   * @returns Metrics matching filter
   */
  getMetricsByStatus(success: boolean): RequestMetrics[] {
    return Array.from(this.metrics.values()).filter(m => m.success === success);
  }

  /**
   * Get metrics for slow requests
   * @param thresholdMs Latency threshold in milliseconds
   * @returns Slow request metrics
   */
  getSlowRequests(thresholdMs: number = 5000): RequestMetrics[] {
    return Array.from(this.metrics.values())
      .filter(m => m.totalDuration > thresholdMs)
      .sort((a, b) => b.totalDuration - a.totalDuration);
  }

  /**
   * Clear all metrics
   */
  clear(): void {
    this.metrics.clear();
  }

  /**
   * Reset all metrics (alias for clear)
   */
  reset(): void {
    this.metrics.clear();
  }

  recordRevalidationTimeout(_endpoint: string): void {
    // no-op: revalidation timeout tracking
  }

  recordRevalidationSuccess(_endpoint: string): void {
    // no-op: revalidation success tracking
  }

  recordRevalidationFailure(_endpoint: string, _error: Error): void {
    // no-op: revalidation failure tracking
  }

  /**
   * Get number of tracked requests
   */
  size(): number {
    return this.metrics.size;
  }

  /**
   * Export metrics as JSON for logging/analysis
   * @param requestId Optional: export single request metrics
   * @returns JSON representation of metrics
   */
  export(requestId?: string): string {
    if (requestId) {
      const metrics = this.metrics.get(requestId);
      return metrics ? JSON.stringify(metrics, null, 2) : '{}';
    }

    return JSON.stringify(
      {
        timestamp: new Date().toISOString(),
        summary: this.getAggregateMetrics(),
        recent: this.getRecentMetrics(20),
      },
      null,
      2
    );
  }

  /**
   * Remove oldest metrics when collection exceeds max size
   */
  private pruneMetricsIfNeeded(): void {
    if (this.metrics.size > this.maxMetricsToKeep) {
      const allMetrics = Array.from(this.metrics.entries());
      // Sort by start time ascending (oldest first)
      allMetrics.sort((a, b) => a[1].startTime - b[1].startTime);

      // Remove oldest entries
      const toRemove = allMetrics.length - this.maxMetricsToKeep;
      for (let i = 0; i < toRemove; i++) {
        this.metrics.delete(allMetrics[i][0]);
      }
    }
  }
}

/**
 * Global metrics collector instance
 * Can be used across the application
 */
export const globalMetricsCollector = new RequestMetricsCollector();
